// Header şablon kaydı (tek kaynak) — admin (gts-web) tarafı.
// gts_ecommerce/src/utils/headerTemplates.ts ile aynı sözleşmeyi taşır.
// Panel bu kayıttan şablon gruplarını ve seçili şablonun yeteneklerini okur;
// böylece kullanıcıya yalnızca ilgili ayarlar gösterilir.

export type HeaderTypeId =
  | 'header1'
  | 'header2'
  | 'header3'
  | 'header4'
  | 'header5'
  | 'header6'
  | 'header7';

export type HeaderGroupId = 'withoutMenu' | 'withMenu';

/**
 * Her şablonun hangi ayar/özellikleri desteklediği.
 * Panel bu bayraklara göre yalnızca ilgili ayarları gösterir.
 */
export interface HeaderCapabilities {
  search: boolean;
  logo: boolean;
  cart: boolean;
  favorites: boolean;
  user: boolean;
  /** Header altında ayrı kategori menüsü (Menu1/2/3) — header1-4 */
  separateMenu: boolean;
  /** Kendi mega menüsünü taşır — header5 */
  builtInMegaMenu: boolean;
  /** Özel üst navigasyon (CustomNavBar) */
  customNav: boolean;
  /** Header 4 promo kartları */
  header4Cards: boolean;
  /** Sipariş butonu — header6 */
  orderButton: boolean;
  /** Header içine gömülü üst menü oluşturucu (header6 / header7) */
  inlineNav: boolean;
  /** Hero üzerine binen şeffaf overlay — header7 */
  overlay: boolean;
  /** Genel CTA butonu (alışveriş) — header7; orderButton alanlarını kullanır */
  ctaButton: boolean;
  /** Telefon / şubeler pill butonları */
  phoneBranches: boolean;
  /** Bilgi şeridi + kayan yazı şeridi */
  promoStrips: boolean;
  /** Cam / blur efekti */
  blur: boolean;
  /** Mobil alt navigasyon */
  bottomNavbar: boolean;
}

/**
 * Storefront içerik boşluğu (spacer) davranışı.
 * Admin tarafında doğrudan kullanılmaz ama sözleşmeyi storefront ile
 * eşit tutmak için kayıtta bulunur.
 */
export interface HeaderChrome {
  mobileFixed: boolean;
  mobileHeight: string;
  stickyMenuSafety: boolean;
}

export interface HeaderTemplate {
  id: HeaderTypeId;
  group: HeaderGroupId;
  /** Geriye dönük uyumluluk için eski isimler (DB'de kalmış olabilir) */
  legacyAliases: string[];
  /** Blur bu şablonda varsayılan açık mı? (header1 ve header5 açık) */
  blurDefaultOn: boolean;
  capabilities: HeaderCapabilities;
  chrome: HeaderChrome;
}

const cap = (overrides: Partial<HeaderCapabilities>): HeaderCapabilities => ({
  search: true,
  logo: true,
  cart: true,
  favorites: true,
  user: true,
  separateMenu: false,
  builtInMegaMenu: false,
  customNav: false,
  header4Cards: false,
  orderButton: false,
  inlineNav: false,
  overlay: false,
  ctaButton: false,
  phoneBranches: true,
  promoStrips: true,
  blur: true,
  bottomNavbar: true,
  ...overrides,
});

export const HEADER_TEMPLATES: Record<HeaderTypeId, HeaderTemplate> = {
  header1: {
    id: 'header1',
    group: 'withoutMenu',
    legacyAliases: ['classic'],
    blurDefaultOn: true,
    capabilities: cap({ separateMenu: true, customNav: true }),
    chrome: { mobileFixed: true, mobileHeight: '4.5rem', stickyMenuSafety: false },
  },
  header2: {
    id: 'header2',
    group: 'withoutMenu',
    legacyAliases: ['modern'],
    blurDefaultOn: false,
    capabilities: cap({ separateMenu: true, customNav: true }),
    chrome: { mobileFixed: true, mobileHeight: '4.5rem', stickyMenuSafety: false },
  },
  header3: {
    id: 'header3',
    group: 'withoutMenu',
    legacyAliases: ['minimal'],
    blurDefaultOn: false,
    capabilities: cap({ separateMenu: true, customNav: true }),
    chrome: { mobileFixed: true, mobileHeight: '5rem', stickyMenuSafety: false },
  },
  header4: {
    id: 'header4',
    group: 'withMenu',
    legacyAliases: ['mega'],
    blurDefaultOn: false,
    capabilities: cap({ separateMenu: true, customNav: true, header4Cards: true }),
    chrome: { mobileFixed: false, mobileHeight: '5.5rem', stickyMenuSafety: true },
  },
  header5: {
    id: 'header5',
    group: 'withMenu',
    legacyAliases: ['tacreo'],
    blurDefaultOn: true,
    capabilities: cap({ builtInMegaMenu: true }),
    chrome: { mobileFixed: false, mobileHeight: '0px', stickyMenuSafety: false },
  },
  header6: {
    id: 'header6',
    group: 'withMenu',
    legacyAliases: ['restoran', 'brand'],
    blurDefaultOn: false,
    capabilities: cap({ customNav: true, orderButton: true, inlineNav: true }),
    chrome: { mobileFixed: false, mobileHeight: '5rem', stickyMenuSafety: true },
  },
  header7: {
    id: 'header7',
    group: 'withMenu',
    legacyAliases: ['centered', 'fashion', 'overlay'],
    blurDefaultOn: true,
    capabilities: cap({
      customNav: true,
      inlineNav: true,
      overlay: true,
      ctaButton: true,
    }),
    chrome: { mobileFixed: false, mobileHeight: '0px', stickyMenuSafety: false },
  },
};

export const HEADER_TEMPLATE_LIST: HeaderTemplate[] = Object.values(HEADER_TEMPLATES);

/** Picker gruplarının gösterim sırası. Başlık/ipuçları i18n'den gelir. */
export const HEADER_GROUP_ORDER: HeaderGroupId[] = ['withoutMenu', 'withMenu'];

/** Belirli bir gruba ait şablonları (kayıt sırasına göre) döndürür. */
export function getHeaderTemplatesByGroup(group: HeaderGroupId): HeaderTemplate[] {
  return HEADER_TEMPLATE_LIST.filter((tpl) => tpl.group === group);
}

/** Legacy alias -> canonical id sözlüğü (kayıttan türetilir) */
const LEGACY_ALIAS_MAP: Record<string, HeaderTypeId> = HEADER_TEMPLATE_LIST.reduce(
  (acc, tpl) => {
    tpl.legacyAliases.forEach((alias) => {
      acc[alias.toLowerCase()] = tpl.id;
    });
    return acc;
  },
  {} as Record<string, HeaderTypeId>
);

export const DEFAULT_HEADER_TYPE: HeaderTypeId = 'header1';

/** Girdiyi geçerli bir HeaderTypeId'ye normalize eder (legacy dahil). */
export function normalizeHeaderTypeId(value?: string | null): HeaderTypeId {
  const raw = String(value || DEFAULT_HEADER_TYPE).toLowerCase();
  if (raw in HEADER_TEMPLATES) {
    return raw as HeaderTypeId;
  }
  return LEGACY_ALIAS_MAP[raw] || DEFAULT_HEADER_TYPE;
}

/** Verilen id/alias için şablon kaydını döndürür (fallback header1). */
export function getHeaderTemplate(value?: string | null): HeaderTemplate {
  return HEADER_TEMPLATES[normalizeHeaderTypeId(value)];
}

/** Şablonun belirli bir yeteneği destekleyip desteklemediği. */
export function headerHasCapability(
  value: string | null | undefined,
  capability: keyof HeaderCapabilities
): boolean {
  return getHeaderTemplate(value).capabilities[capability] === true;
}

const TRANSPARENT_BACKGROUNDS = new Set(['transparent', 'rgba(0,0,0,0)', '#00000000', '#0000']);

/** Header 7 overlay: arka plan şeffaf kaydedildiğinde aktif kabul edilir. */
export function isHeaderOverlayActive(settings?: {
  headerType?: string | null;
  backgroundColor?: string | null;
} | null): boolean {
  if (!headerHasCapability(settings?.headerType, 'overlay')) return false;
  const bg = String(settings?.backgroundColor || '').trim().toLowerCase();
  return TRANSPARENT_BACKGROUNDS.has(bg);
}
